<template>
    <div>
        <LoadingComponent :props="loading" />
        <div class="flex items-center justify-between gap-3 mb-3">
            <h4 class="font-medium text-lg text-heading leading-5">{{ $t('menu.order_statistics') }}</h4>
            <div class="relative cursor-pointer">
                <Datepicker hideInputIcon autoApply :enableTimePicker="false" utc="false" 
                    :placeholder="$t('label.select_date_range')" @update:modelValue="handleDate" v-model="modelValue"
                    :range="true">
                </Datepicker>
            </div>
        </div>
        <draggable v-model="cardsList" item-key="key" class="row" :disabled="!isReorderModeActive" @change="saveCardsOrder">
            <template #item="{ element: card }">
                <div class="col-12 sm:col-6 md:col-4 lg:col-6 xl:col-3">
                    <div class="flex items-center justify-between p-4 rounded-lg border bg-white shadow-2xs hover:shadow transition-shadow relative"
                        :class="{ 'cursor-move ring-2 ring-primary/40 bg-gray-50/50': isReorderModeActive }">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full flex items-center justify-center">
                                <i :class="card.icon" class="text-heading text-2xl lab-font-size-24 bg-gray-100 p-2.5 rounded-full"></i>
                            </div>
                            <div class="pl-3 py-1 border-l">
                                <h3 class="font-medium text-heading font-client text-xs">{{ $t(card.label) }}</h3>
                                <h4 class="font-medium font-numbers text-2xl leading-8 text-heading">{{ getCardValue(card.key) }}</h4>
                            </div>
                        </div>
                        <i v-if="isReorderModeActive" class="lab-line-menu text-gray-400 text-lg cursor-move flex-shrink-0" :title="$t('label.drag_to_reorder')"></i>
                    </div>
                </div>
            </template>
        </draggable>
    </div>
</template>

<script>
import LoadingComponent from "../components/LoadingComponent";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import draggable from "vuedraggable";

export default {
    name: "OrderStatisticsComponent",
    components: { LoadingComponent, Datepicker, draggable },
    emits: ['activity'],
    props: {
        isReorderModeActive: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            first_date: null,
            last_date: null,
            total_order: null,
            pending_order: null,
            confirmed_order: null,
            ongoing_order: null,
            delivered_order: null,
            canceled_order: null,
            returned_order: null,
            rejected_order: null,
            modelValue: null,
            cardsList: [
                { key: 'total_order', label: 'label.total_orders', icon: 'lab-fill-box' },
                { key: 'pending_order', label: 'label.pending', icon: 'lab-fill-box-time' },
                { key: 'confirmed_order', label: 'label.confirmed', icon: 'lab-fill-box-search' },
                { key: 'ongoing_order', label: 'label.ongoing', icon: 'lab-fill-shipping' },
                { key: 'delivered_order', label: 'label.delivered', icon: 'lab-fill-box-tick' },
                { key: 'canceled_order', label: 'label.canceled', icon: 'lab-fill-box-cross' },
                { key: 'returned_order', label: 'label.returned', icon: 'lab-fill-box-rotate' },
                { key: 'rejected_order', label: 'label.rejected', icon: 'lab-fill-box-cross' },
            ]
        };
    },
    mounted() {
        this.orderStatistic();
        this.loadSavedCardsOrder();
    },
    methods: {
        loadSavedCardsOrder: function () {
            try {
                const saved = localStorage.getItem('admin_dashboard_order_stats_cards');
                if (saved) {
                    const keys = JSON.parse(saved);
                    const map = new Map(this.cardsList.map(c => [c.key, c]));
                    const ordered = keys.map(k => map.get(k)).filter(Boolean);
                    this.cardsList.forEach(c => { if (!ordered.includes(c)) ordered.push(c); });
                    this.cardsList = ordered;
                }
            } catch (e) {
                console.error("Error loading order stats cards order", e);
            }
        },
        saveCardsOrder: function () {
            try {
                const keys = this.cardsList.map(c => c.key);
                localStorage.setItem('admin_dashboard_order_stats_cards', JSON.stringify(keys));
                this.$emit('activity');
            } catch (e) {
                console.error("Error saving order stats cards order", e);
            }
        },
        getCardValue: function (key) {
            return this[key] ?? '-';
        },
        handleDate: function (e) {
            if (e) {
                this.first_date = e[0];
                this.last_date = e[1];

                this.loading.isActive = true;
                this.$store.dispatch("dashboard/orderStatistics", {
                    first_date: this.first_date,
                    last_date: this.last_date,
                }).then((res) => {
                    this.total_order = res.data.data.total_order;
                    this.pending_order = res.data.data.pending_order;
                    this.confirmed_order = res.data.data.confirmed_order;
                    this.ongoing_order = res.data.data.ongoing_order;
                    this.delivered_order = res.data.data.delivered_order;
                    this.canceled_order = res.data.data.canceled_order;
                    this.returned_order = res.data.data.returned_order;
                    this.rejected_order = res.data.data.rejected_order;
                    this.loading.isActive = false;
                }).catch((err) => {
                    this.loading.isActive = false;
                });
            } else {
                this.date = null;
                this.first_date = null;
                this.last_date = null;
                this.orderStatistic();
            }
        },
        orderStatistic: function () {
            this.loading.isActive = true;
            this.$store.dispatch("dashboard/orderStatistics").then((res) => {
                this.total_order = res.data.data.total_order;
                this.pending_order = res.data.data.pending_order;
                this.confirmed_order = res.data.data.confirmed_order;
                this.ongoing_order = res.data.data.ongoing_order;
                this.delivered_order = res.data.data.delivered_order;
                this.canceled_order = res.data.data.canceled_order;
                this.returned_order = res.data.data.returned_order;
                this.rejected_order = res.data.data.rejected_order;
                this.loading.isActive = false;
            }).catch((err) => {
                this.loading.isActive = false;
            });
        }
    }
}
</script>
